'use client';

import {
	AlertTriangle,
	BarChart3,
	CheckCircle2,
	Clock3,
	GitBranch,
	Plus,
	RefreshCw,
	ShieldCheck,
	SlidersHorizontal,
	TrendingUp,
	Users
} from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	CreateOrganizationInsightSnapshotRequest,
	OrganizationDepartmentInsight,
	OrganizationInsightsChecklistItem,
	OrganizationInsightsChecklistStatus,
	OrganizationInsightsPlaybook,
	OrganizationInsightsRiskItem,
	OrganizationInsightsRiskSeverity,
	OrganizationInsightsSummaryResponse,
	OrganizationTrendPoint
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	EmptyState,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type SnapshotFormState = {
	periodMonth: string;
	departmentName: string;
	headcount: string;
	activeHeadcount: string;
	joinedCount: string;
	resignedCount: string;
	payrollBudgetKrw: string;
	goalAchievementPercent: string;
	retentionRatePercent: string;
	averageOvertimeHours: string;
	leaveUsageHours: string;
	note: string;
};

type InsightFilterState = {
	periodMonth: string;
	departmentName: string;
};

type OrganizationInsightsReadiness = NonNullable<OrganizationInsightsSummaryResponse['readiness']>;
type OrganizationInsightsReliabilityLabel = OrganizationInsightsReadiness['reliabilityLabel'];

function currentMonth(): string {
	return new Date().toISOString().slice(0, 7);
}

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function moneyLabel(value: number | null | undefined): string {
	return `${numberLabel(value ?? 0)}원`;
}

function decimalLabel(value: number | null | undefined, suffix = 'h'): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return `0.0${suffix}`;
	return `${value.toFixed(1)}${suffix}`;
}

function checklistStatusLabel(status: OrganizationInsightsChecklistStatus): string {
	if (status === 'READY') return '준비';
	if (status === 'WARNING') return '주의';
	return '대기';
}

function checklistVariant(status: OrganizationInsightsChecklistStatus): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function riskVariant(severity: OrganizationInsightsRiskSeverity): 'success' | 'warning' | 'outline' {
	if (severity === 'READY') return 'success';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function healthVariant(label: string): 'success' | 'warning' | 'outline' {
	if (label === '양호') return 'success';
	if (label === '주의' || label === '관리자 필요') return 'warning';
	return 'outline';
}

function reliabilityLabel(label: OrganizationInsightsReliabilityLabel): string {
	if (label === 'HIGH') return '높은 신뢰';
	if (label === 'PARTIAL') return '부분 신뢰';
	return '데이터 부족';
}

function reliabilityVariant(label: OrganizationInsightsReliabilityLabel): 'success' | 'warning' | 'outline' {
	if (label === 'HIGH') return 'success';
	if (label === 'PARTIAL') return 'warning';
	return 'outline';
}

function summaryPath(filters: InsightFilterState): string {
	const params = new URLSearchParams();
	if (filters.periodMonth) params.set('periodMonth', filters.periodMonth);
	if (filters.departmentName) params.set('departmentName', filters.departmentName);
	const query = params.toString();
	return query ? `/organization/insights/summary?${query}` : '/organization/insights/summary';
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function ChecklistGrid({ items }: { items: OrganizationInsightsChecklistItem[] }) {
	if (items.length === 0) return <EmptyState message="조직 인사이트 체크리스트가 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
			{items.map((item) => {
				const Icon = item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
				return (
					<div key={item.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="break-keep font-semibold">{item.label}</p>
									<p className="mt-1 break-keep text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={checklistVariant(item.status)}>{checklistStatusLabel(item.status)}</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RiskPanel({ risks }: { risks: OrganizationInsightsRiskItem[] }) {
	if (risks.length === 0) return <EmptyState message="표시할 조직 리스크가 없습니다." />;
	return (
		<div className="min-w-0 rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">조직 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={riskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function DepartmentTable({ departments }: { departments: OrganizationDepartmentInsight[] }) {
	if (departments.length === 0) return <EmptyState message="조직 인사이트 대상 부서가 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[980px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">조직</th>
						<th className="px-4 py-3 font-medium">인원</th>
						<th className="px-4 py-3 font-medium">목표 / 유지율</th>
						<th className="px-4 py-3 font-medium">업무부하</th>
						<th className="px-4 py-3 font-medium">급여 예산</th>
						<th className="px-4 py-3 font-medium">상태</th>
					</tr>
				</thead>
				<tbody>
					{departments.map((department) => (
						<tr key={department.departmentId} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<p className="font-semibold">{department.departmentName}</p>
								<p className="mt-1 text-xs text-muted-foreground">{department.managerName ?? '관리자 미지정'}</p>
							</td>
							<td className="px-4 py-4">
								<p className="font-semibold">{numberLabel(department.currentHeadcount)}명</p>
								<p className="mt-1 text-xs text-muted-foreground">
									스냅샷 {numberLabel(department.snapshotHeadcount)}명 · 차이 {numberLabel(department.headcountDelta)}
								</p>
							</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap gap-2">
									<Badge variant={department.goalAchievementPercent >= 80 ? 'success' : 'warning'}>
										목표 {numberLabel(department.goalAchievementPercent)}%
									</Badge>
									<Badge variant={department.retentionRatePercent >= 90 ? 'success' : 'warning'}>
										유지 {numberLabel(department.retentionRatePercent)}%
									</Badge>
								</div>
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								<p>초과 {decimalLabel(department.averageOvertimeHours)}</p>
								<p className="mt-1 text-xs">휴가 {decimalLabel(department.leaveUsageHours)}</p>
							</td>
							<td className="px-4 py-4 font-medium">{moneyLabel(department.payrollBudgetKrw)}</td>
							<td className="px-4 py-4">
								<Badge variant={healthVariant(department.healthLabel)}>{department.healthLabel}</Badge>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function TrendTable({ points }: { points: OrganizationTrendPoint[] }) {
	if (points.length === 0) return <EmptyState message="월별 조직 스냅샷이 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[920px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">월 / 조직</th>
						<th className="px-4 py-3 font-medium">인원 변화</th>
						<th className="px-4 py-3 font-medium">목표 / 유지</th>
						<th className="px-4 py-3 font-medium">초과근무 / 휴가</th>
						<th className="px-4 py-3 font-medium">급여 예산</th>
					</tr>
				</thead>
				<tbody>
					{points.map((point) => (
						<tr key={`${point.periodMonth}-${point.departmentName}-${point.headcount}`} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<p className="font-semibold">{point.periodMonth}</p>
								<p className="mt-1 text-xs text-muted-foreground">{point.departmentName}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								<p>{numberLabel(point.headcount)}명</p>
								<p className="mt-1 text-xs">
									입사 {numberLabel(point.joinedCount)} · 퇴사 {numberLabel(point.resignedCount)}
								</p>
							</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap gap-2">
									<Badge variant="outline">목표 {numberLabel(point.goalAchievementPercent)}%</Badge>
									<Badge variant="outline">유지 {numberLabel(point.retentionRatePercent)}%</Badge>
								</div>
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{decimalLabel(point.averageOvertimeHours)} / {decimalLabel(point.leaveUsageHours)}
							</td>
							<td className="px-4 py-4 font-medium">{moneyLabel(point.payrollBudgetKrw)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function PlaybookGrid({ playbooks }: { playbooks: OrganizationInsightsPlaybook[] }) {
	if (playbooks.length === 0) return <EmptyState message="조직 운영 플레이북이 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
			{playbooks.map((playbook) => (
				<div key={playbook.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<ShieldCheck className="size-4" />
					</div>
					<p className="mt-3 break-keep font-semibold">{playbook.label}</p>
					<p className="mt-1 break-keep text-sm text-muted-foreground">{playbook.description}</p>
				</div>
			))}
		</div>
	);
}

function ReadinessPanel({
	data,
	filters,
	setFilters
}: {
	data: OrganizationInsightsSummaryResponse;
	filters: InsightFilterState;
	setFilters: (filters: InsightFilterState) => void;
}) {
	const readiness = data.readiness;
	const benchmark = data.benchmark;
	const filterOptions = data.filterOptions;
	const accessPolicy = data.accessPolicy;
	if (!readiness && !benchmark && !filterOptions && !accessPolicy) return null;
	return (
		<Card className="min-w-0">
			<CardHeader className="gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>분석 준비도</CardTitle>
					<CardDescription>
						권한, 기간, 조직 필터와 전월 비교 기준을 함께 확인합니다.
					</CardDescription>
				</div>
				{readiness ? (
					<Badge variant={reliabilityVariant(readiness.reliabilityLabel)}>
						{reliabilityLabel(readiness.reliabilityLabel)}
					</Badge>
				) : null}
			</CardHeader>
			<CardContent className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
				<div className="grid gap-3 sm:grid-cols-2">
					<div className="rounded-lg border border-border p-3">
						<p className="text-xs font-medium text-muted-foreground">스냅샷 커버리지</p>
						<p className="mt-2 text-xl font-semibold tracking-normal">
							{numberLabel(readiness?.snapshotCoveragePercent)}%
						</p>
						<p className="mt-1 text-xs text-muted-foreground">{readiness?.actionLabel ?? '분석 기준 확인 필요'}</p>
					</div>
					<div className="rounded-lg border border-border p-3">
						<p className="text-xs font-medium text-muted-foreground">필터 커버리지</p>
						<p className="mt-2 text-xl font-semibold tracking-normal">
							{numberLabel(readiness?.filterCoveragePercent)}%
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							{numberLabel(readiness?.periodCount)}개 기간 · {numberLabel(readiness?.filterableDepartmentCount)}개 조직
						</p>
					</div>
					<div className="rounded-lg border border-border p-3">
						<p className="text-xs font-medium text-muted-foreground">전월 대비 초과근무</p>
						<p className="mt-2 text-xl font-semibold tracking-normal">
							{numberLabel(benchmark?.overtimeDeltaPercent)}%
						</p>
						<p className="mt-1 text-xs text-muted-foreground">전월 {benchmark?.previousPeriodMonth ?? '-'}</p>
						<p className="mt-1 text-xs text-muted-foreground">
							표준편차 {decimalLabel(benchmark?.overtimeStandardDeviation)}
						</p>
					</div>
					<div className="rounded-lg border border-border p-3">
						<p className="text-xs font-medium text-muted-foreground">권한 범위</p>
						<p className="mt-2 text-sm font-semibold">{accessPolicy?.visibilityLabel ?? '관리자 전용'}</p>
						<p className="mt-1 text-xs text-muted-foreground">
							{accessPolicy?.requiredRoles.join(', ') ?? 'OWNER, ADMIN'}
						</p>
					</div>
				</div>
				<div className="rounded-lg border border-border bg-background p-4">
					<div className="flex items-center gap-2">
						<SlidersHorizontal className="size-4 text-muted-foreground" />
						<p className="font-semibold">분석 필터</p>
					</div>
					<div className="mt-4 grid gap-3 sm:grid-cols-2">
						<div>
							<Label htmlFor="organization-insight-period-filter">분석 기간</Label>
							<select
								id="organization-insight-period-filter"
								value={filters.periodMonth}
								onChange={(event) => setFilters({ ...filters, periodMonth: event.target.value })}
								className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 mt-2 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
							>
								<option value="">최신 기간</option>
								{filterOptions?.periodMonths.map((periodMonth) => (
									<option key={periodMonth} value={periodMonth}>
										{periodMonth}
									</option>
								))}
							</select>
						</div>
						<div>
							<Label htmlFor="organization-insight-department-filter">대상 조직</Label>
							<select
								id="organization-insight-department-filter"
								value={filters.departmentName}
								onChange={(event) => setFilters({ ...filters, departmentName: event.target.value })}
								className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 mt-2 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
							>
								<option value="">전체 조직</option>
								{filterOptions?.departments.map((department) => (
									<option key={department} value={department}>
										{department}
									</option>
								))}
							</select>
						</div>
					</div>
					{accessPolicy?.dataScopeDescription ? (
						<p className="mt-3 text-xs leading-5 text-muted-foreground">{accessPolicy.dataScopeDescription}</p>
					) : null}
				</div>
			</CardContent>
		</Card>
	);
}

export function OrganizationInsightsPage() {
	const [state, setState] = useState<RequestState<OrganizationInsightsSummaryResponse>>({ status: 'idle' });
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [filters, setFilters] = useState<InsightFilterState>({ periodMonth: '', departmentName: '' });
	const [form, setForm] = useState<SnapshotFormState>({
		periodMonth: currentMonth(),
		departmentName: '',
		headcount: '0',
		activeHeadcount: '0',
		joinedCount: '0',
		resignedCount: '0',
		payrollBudgetKrw: '0',
		goalAchievementPercent: '0',
		retentionRatePercent: '100',
		averageOvertimeHours: '0.0',
		leaveUsageHours: '0.0',
		note: ''
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<OrganizationInsightsSummaryResponse>(summaryPath(filters));
				setState({ status: 'success', data: summary });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[filters]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function submitSnapshot(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitting(true);
		setActionMessage(null);
		try {
			const request: CreateOrganizationInsightSnapshotRequest = {
				periodMonth: form.periodMonth,
				departmentName: form.departmentName.trim(),
				headcount: Number(form.headcount),
				activeHeadcount: Number(form.activeHeadcount),
				joinedCount: Number(form.joinedCount),
				resignedCount: Number(form.resignedCount),
				payrollBudgetKrw: Number(form.payrollBudgetKrw),
				goalAchievementPercent: Number(form.goalAchievementPercent),
				retentionRatePercent: Number(form.retentionRatePercent),
				averageOvertimeHours: Number(form.averageOvertimeHours),
				leaveUsageHours: Number(form.leaveUsageHours),
				note: form.note.trim() || null
			};
			await api.post('/organization/insights/snapshots', request);
			setForm((current) => ({ ...current, departmentName: '', note: '' }));
			setActionMessage('조직 트렌드 스냅샷을 저장했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setSubmitting(false);
		}
	}

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="min-w-0 space-y-6 overflow-x-hidden">
			<PageHeader
				title="조직 인사이트"
				description="조직도 기준정보와 월별 조직 트렌드로 인원, 급여 예산, 목표 달성, 유지율, 초과근무, 휴가 사용을 점검합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionMessage ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{actionMessage}</div>
			) : null}

			{data ? (
				<>
					<ReadinessPanel data={data} filters={filters} setFilters={setFilters} />

					<div className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-4">
						<StatCard
							title="조직 / 인원"
							value={`${numberLabel(data.activeDepartmentCount)} / ${numberLabel(data.activeHeadcount)}`}
							description={`전체 ${numberLabel(data.departmentCount)}개 조직 · ${data.latestPeriodMonth}`}
							icon={GitBranch}
						/>
						<StatCard
							title="관리자 커버리지"
							value={`${numberLabel(data.managerCoveragePercent)}%`}
							description={`미배정 구성원 ${numberLabel(data.unassignedHeadcount)}명`}
							icon={Users}
						/>
						<StatCard
							title="유지율 / 목표"
							value={`${numberLabel(data.retentionRatePercent)}% / ${numberLabel(data.goalAchievementPercent)}%`}
							description="최신 조직 스냅샷 평균"
							icon={TrendingUp}
						/>
						<StatCard
							title="급여 예산"
							value={moneyLabel(data.payrollBudgetKrw)}
							description={`초과 ${decimalLabel(data.averageOvertimeHours)} · 휴가 ${decimalLabel(data.leaveUsageHours)}`}
							icon={BarChart3}
						/>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>월별 스냅샷 입력</CardTitle>
								<CardDescription>조직 트렌드 비교에 필요한 인원, 급여, 목표, 유지율, 초과근무, 휴가 지표를 저장합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3" onSubmit={submitSnapshot}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="org-insight-month">월</Label>
											<Input
												id="org-insight-month"
												type="month"
												value={form.periodMonth}
												onChange={(event) => setForm((current) => ({ ...current, periodMonth: event.target.value }))}
												required
											/>
										</div>
										<div>
											<Label htmlFor="org-insight-department">조직명</Label>
											<Input
												id="org-insight-department"
												value={form.departmentName}
												onChange={(event) =>
													setForm((current) => ({ ...current, departmentName: event.target.value }))
												}
												placeholder="예: 제품"
												required
											/>
										</div>
									</div>
									<div className="grid gap-3 sm:grid-cols-4">
										<Input
											aria-label="총 인원"
											type="number"
											min={0}
											value={form.headcount}
											onChange={(event) => setForm((current) => ({ ...current, headcount: event.target.value }))}
										/>
										<Input
											aria-label="재직 인원"
											type="number"
											min={0}
											value={form.activeHeadcount}
											onChange={(event) =>
												setForm((current) => ({ ...current, activeHeadcount: event.target.value }))
											}
										/>
										<Input
											aria-label="입사 인원"
											type="number"
											min={0}
											value={form.joinedCount}
											onChange={(event) => setForm((current) => ({ ...current, joinedCount: event.target.value }))}
										/>
										<Input
											aria-label="퇴사 인원"
											type="number"
											min={0}
											value={form.resignedCount}
											onChange={(event) => setForm((current) => ({ ...current, resignedCount: event.target.value }))}
										/>
									</div>
									<div className="grid gap-3 sm:grid-cols-3">
										<Input
											aria-label="급여 예산"
											type="number"
											min={0}
											value={form.payrollBudgetKrw}
											onChange={(event) =>
												setForm((current) => ({ ...current, payrollBudgetKrw: event.target.value }))
											}
										/>
										<Input
											aria-label="목표 달성률"
											type="number"
											min={0}
											max={100}
											value={form.goalAchievementPercent}
											onChange={(event) =>
												setForm((current) => ({ ...current, goalAchievementPercent: event.target.value }))
											}
										/>
										<Input
											aria-label="유지율"
											type="number"
											min={0}
											max={100}
											value={form.retentionRatePercent}
											onChange={(event) =>
												setForm((current) => ({ ...current, retentionRatePercent: event.target.value }))
											}
										/>
									</div>
									<div className="grid gap-3 sm:grid-cols-2">
										<Input
											aria-label="평균 초과근무"
											type="number"
											min={0}
											step="0.1"
											value={form.averageOvertimeHours}
											onChange={(event) =>
												setForm((current) => ({ ...current, averageOvertimeHours: event.target.value }))
											}
										/>
										<Input
											aria-label="휴가 사용시간"
											type="number"
											min={0}
											step="0.1"
											value={form.leaveUsageHours}
											onChange={(event) =>
												setForm((current) => ({ ...current, leaveUsageHours: event.target.value }))
											}
										/>
									</div>
									<textarea
										value={form.note}
										onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
										className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 min-h-20 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
										placeholder="조직 변경 사유, 리더십 액션, 채용 계획"
									/>
									<Button type="submit" disabled={submitting}>
										<Plus className="size-4" />
										스냅샷 저장
									</Button>
								</form>
							</CardContent>
						</Card>

						<div className="grid min-w-0 gap-4">
							<Card className="min-w-0">
								<CardHeader>
									<CardTitle>운영 체크리스트</CardTitle>
									<CardDescription>조직도, 관리자, 월별 스냅샷, 유지율, 목표 달성 연결성을 확인합니다.</CardDescription>
								</CardHeader>
								<CardContent>
									<ChecklistGrid items={data.checklist} />
								</CardContent>
							</Card>
							<RiskPanel risks={data.riskItems} />
						</div>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>조직별 인사이트</CardTitle>
								<CardDescription>현재 조직도와 최신 스냅샷을 비교해 인원 차이와 운영 리스크를 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<DepartmentTable departments={data.departmentInsights} />
							</CardContent>
						</Card>
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>월별 조직 트렌드</CardTitle>
								<CardDescription>조직별 인원, 목표, 유지율, 초과근무, 휴가, 급여 예산 추이를 비교합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<TrendTable points={data.trendPoints} />
							</CardContent>
						</Card>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>조직 운영 플레이북</CardTitle>
							<CardDescription>조직 변화, 관리 범위, 유지율, 업무부하, 휴가 계획을 실행 단위로 점검합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<PlaybookGrid playbooks={data.playbooks} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}
